import type { ActiveTrade } from '@/lib/types'
import { useNow } from '@/hooks/useNow'
import {
  formatUsd,
  formatPrice,
  fundingPct,
  spreadPct,
  formatCountdown,
  formatDuration,
  formatDay,
  formatClock,
  isFundingSoon,
  tradeUrl,
  exchangeLabel,
  exchangeColor,
  pnlSign,
  pctSigned,
} from '@/lib/format'
import { linkStatusLabel, linkStatusTone } from './model'
import { EmptyState } from '@/components/common/EmptyState'
import { ArrowUpIcon, ArrowDownIcon, LinkIcon } from '@/components/common/icons'

interface ActiveTradesTableProps {
  trades: ActiveTrade[]
}

/** Нога в колонке «Биржи»: стрелка направления + имя биржи + deep-link на фьючерс. */
function LegEx({ exchange, coin, dir }: { exchange: string; coin: string; dir: 'long' | 'short' }) {
  const url = tradeUrl(exchange, coin)
  const Arrow = dir === 'long' ? ArrowUpIcon : ArrowDownIcon
  const inner = (
    <>
      <span className={`leg-arrow leg-arrow--${dir}`}>
        <Arrow size={13} />
      </span>
      <span
        className="ex-chip__dot"
        style={{ background: exchangeColor(exchange) }}
        aria-hidden="true"
      />
      <span className="leg-ex__name">{exchangeLabel(exchange)}</span>
    </>
  )
  if (url) {
    return (
      <a className="leg-ex leg-ex--link" href={url} target="_blank" rel="noopener noreferrer">
        {inner}
        <LinkIcon size={12} className="leg-ex__link-ico" />
      </a>
    )
  }
  return <span className="leg-ex">{inner}</span>
}

export function ActiveTradesTable({ trades }: ActiveTradesTableProps) {
  const nowMs = useNow(1000)

  if (trades.length === 0) {
    return (
      <EmptyState
        icon={<LinkIcon size={22} />}
        title="Нет открытых связок"
        text="Когда бот откроет дельта-нейтральную связку, она появится здесь с текущим PnL."
      />
    )
  }

  return (
    <div className="tbl-wrap">
      <table className="tbl tbl--trades">
        <thead>
          <tr>
            <th className="col-lead">Монета</th>
            <th className="col-lead">Биржи</th>
            <th>Вход</th>
            <th>Объём</th>
            <th>Фандинг</th>
            <th>Фандинг итог</th>
            <th>До фандинга</th>
            <th>Текущий PNL</th>
            <th>Время</th>
            <th>Статус</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => {
            const sign = pnlSign(t.current_pnl_usd)
            const soonLong = isFundingSoon(t.next_funding_ts_ms_long, nowMs)
            const soonShort = isFundingSoon(t.next_funding_ts_ms_short, nowMs)
            const tone = linkStatusTone(t.status)
            return (
              <tr key={t.link_id}>
                <td className="col-lead">
                  <div className="coin">
                    <span className="coin__ico">{t.coin.slice(0, 3)}</span>
                    <div>
                      <div className="coin__name">{t.coin}</div>
                      <div className="coin__sym">
                        {t.symbol.base}/{t.symbol.quote}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="col-lead">
                  <div className="leg-stack">
                    <LegEx exchange={t.long_exchange} coin={t.coin} dir="long" />
                    <LegEx exchange={t.short_exchange} coin={t.coin} dir="short" />
                  </div>
                </td>
                <td>
                  <div className="leg-stack tnum">
                    <span>{formatPrice(t.long_entry_price)}</span>
                    <span>{formatPrice(t.short_entry_price)}</span>
                  </div>
                </td>
                <td className="tnum">{formatUsd(t.notional_usd, { digits: 0 })}</td>
                <td>
                  <div className="leg-stack tnum">
                    <span className={`fund fund--${pnlSign(t.funding_rate_long)}`}>
                      {fundingPct(t.funding_rate_long)}
                    </span>
                    <span className={`fund fund--${pnlSign(t.funding_rate_short)}`}>
                      {fundingPct(t.funding_rate_short)}
                    </span>
                  </div>
                </td>
                <td className="tnum">
                  <span className={`fund fund--${pnlSign(t.net_funding_normalized)}`}>
                    {spreadPct(t.net_funding_normalized)}
                  </span>
                </td>
                <td className="tnum">
                  <div className="leg-stack">
                    <span className={`countdown${soonLong ? ' countdown--soon' : ''}`}>
                      {formatCountdown(t.next_funding_ts_ms_long, nowMs)}
                    </span>
                    <span className={`countdown${soonShort ? ' countdown--soon' : ''}`}>
                      {formatCountdown(t.next_funding_ts_ms_short, nowMs)}
                    </span>
                  </div>
                </td>
                <td className="tnum">
                  <div className={`pnl pnl--${sign}`}>
                    <span className="pnl__usd">{formatUsd(t.current_pnl_usd, { signed: true })}</span>
                    <span className="pnl__pct">{pctSigned(t.current_pnl_pct)}</span>
                  </div>
                </td>
                <td className="tnum">
                  <div className="stamp">
                    <span>{formatDuration(Math.max(0, nowMs - t.opened_ts_ms))}</span>
                    <span className="stamp__day">
                      {formatDay(t.opened_ts_ms)} {formatClock(t.opened_ts_ms)}
                    </span>
                  </div>
                </td>
                <td>
                  <span className={`badge badge--${tone}`}>{linkStatusLabel(t.status)}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
